import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE_COLOR = {
  Wedding: "bg-burgundy",
  "Technical Meeting": "bg-blue-500",
  "Vendor Meeting": "bg-violet-500",
  "Client Meeting": "bg-emerald-500",
  Meeting: "bg-gold",
  "Payment Due": "bg-red-500",
  Preparation: "bg-amber-500",
};

export default function MiniCalendar() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => { api.get("/calendar").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const byDate = useMemo(() => {
    const map = {};
    items.forEach((i) => { (map[i.date] = map[i.date] || []).push(i); });
    return map;
  }, [items]);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = cursor.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const todayStr = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="card-lux p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-1.5 rounded-lg hover:bg-beige" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-burgundy" /></button>
        <p className="font-heading text-lg text-[#1A1A1A] capitalize">{monthName}</p>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-1.5 rounded-lg hover:bg-beige" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-burgundy" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["M", "S", "S", "R", "K", "J", "S"].map((d, i) => <div key={i} className="text-[10px] font-semibold text-[#7A7A7A] py-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const evs = byDate[dateStr] || [];
          const isToday = dateStr === todayStr;
          return (
            <div key={i} className={cn("aspect-square flex flex-col items-center justify-center rounded-lg text-xs relative", isToday ? "bg-burgundy text-white font-bold" : "hover:bg-beige text-[#4A4A4A]")}>
              {d}
              {evs.length > 0 && (
                <div className="flex gap-0.5 absolute bottom-1">
                  {evs.slice(0, 3).map((e, j) => <span key={j} className={cn("w-1 h-1 rounded-full", isToday ? "bg-white" : (TYPE_COLOR[e.type] || "bg-gold"))} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gold/20">
        {Object.entries({ Wedding: "bg-burgundy", Meeting: "bg-blue-500", "Payment Due": "bg-red-500" }).map(([k, c]) => (
          <span key={k} className="flex items-center gap-1 text-[10px] text-[#7A7A7A]"><span className={cn("w-2 h-2 rounded-full", c)} />{k}</span>
        ))}
      </div>
    </div>
  );
}
